import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["toolbarControls", "responsive", "multiRow"];
import * as ToolbarControls from 'tinymce-plugins/constants/toolbar';
import { ToolbarControlTypes, HSCTA_SCOPE, HS_WEB_INTERACTIVES_SCOPE } from '../Constants';
import { hasScope } from '../utils/auth';
import { includesToolbarControl, getToolbarControls } from '../utils/toolbar';

// Format controls with prioritization for folding into the MORE menu
function getFormatControls(toolbarControls, responsive, options) {
  const {
    hideFontFamily,
    hideFontSize,
    useListButtons,
    useSourceCodeButton
  } = options;
  let formatControls = [];
  if (!hideFontFamily) {
    formatControls.push({
      type: ToolbarControlTypes.SELECT,
      control: ToolbarControls.CANVAS_FONT_FAMILY,
      priority: 8
    });
  }
  if (!hideFontSize) {
    formatControls.push({
      type: ToolbarControlTypes.SELECT,
      control: ToolbarControls.CANVAS_FONT_SIZE,
      priority: 7
    });
  }
  formatControls.push({
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_BOLD,
    priority: 1
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_ITALIC,
    priority: 2
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_UNDERLINE,
    priority: 3
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_TEXT_COLOR,
    priority: 4
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_BACKGROUND_COLOR,
    priority: 4
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_ALIGNMENT,
    priority: 5
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: useListButtons ? ToolbarControls.CANVAS_BULLETED_LIST_BUTTON : ToolbarControls.CANVAS_BULLETED_LIST,
    priority: 6
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: useListButtons ? ToolbarControls.CANVAS_NUMBERED_LIST_BUTTON : ToolbarControls.CANVAS_NUMBERED_LIST,
    priority: 6
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_LINEHEIGHT,
    priority: 9
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_OUTDENT,
    priority: 10
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_INDENT,
    priority: 10
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_REMOVE_FORMAT,
    priority: 11
  });
  formatControls = formatControls.filter(({
    control
  }) => includesToolbarControl(toolbarControls, control));
  const menuItems = [ToolbarControls.CANVAS_STRIKETHROUGH, ToolbarControls.CANVAS_SUPERSCRIPT, ToolbarControls.CANVAS_SUBSCRIPT, ToolbarControls.CANVAS_CODE_FORMAT];
  if (useSourceCodeButton) {
    menuItems.push(ToolbarControls.CANVAS_SOURCE_CODE_BUTTON);
  }
  const menuControl = {
    type: ToolbarControlTypes.MORE_MENU,
    menuItems: menuItems.filter(control => includesToolbarControl(toolbarControls, control))
  };
  let hasMoreMenu = false;
  if (responsive && formatControls.length > 4 || menuControl.menuItems.length) {
    formatControls.push(menuControl);
    hasMoreMenu = true;
  }
  return {
    formatControls,
    hasMoreMenu
  };
}
function getInsertControls(toolbarControls, {
  showRssTokens,
  useCharmapButton,
  useSnippet,
  useContentGeneration,
  hsContentAssistant
}) {
  const insertControls = [{
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_LINK
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_EMOJI
  }, {
    type: ToolbarControlTypes.BUTTON,
    control: ToolbarControls.CANVAS_IMAGE
  }];
  if (useContentGeneration && hsContentAssistant) {
    insertControls.push({
      type: ToolbarControlTypes.BUTTON,
      control: ToolbarControls.CANVAS_CONTENT_GENERATION
    });
  }
  if (useSnippet) {
    insertControls.push({
      type: ToolbarControlTypes.BUTTON,
      control: ToolbarControls.CANVAS_SNIPPET
    });
  }
  if (useCharmapButton) {
    insertControls.push({
      type: ToolbarControlTypes.BUTTON,
      control: ToolbarControls.CANVAS_CHARMAP_BUTTON
    });
  }
  if (showRssTokens) {
    insertControls.push({
      type: ToolbarControlTypes.BUTTON,
      control: ToolbarControls.CANVAS_RSS_TOKEN
    });
  }
  return insertControls.filter(({
    control
  }) => includesToolbarControl(toolbarControls, control));
}
function getInsertMenu(toolbarControls, {
  editContext,
  contentType,
  scopes = [],
  useEmbed,
  useVideo,
  useCallout,
  useCharmapButton,
  useIcon,
  useNewCtas
}) {
  const insertMenu = [];
  if (hasScope(HSCTA_SCOPE, scopes) || useNewCtas && hasScope(HS_WEB_INTERACTIVES_SCOPE, scopes)) {
    insertMenu.push(ToolbarControls.CANVAS_CTA);
  }
  if (contentType !== 'email' && useEmbed) {
    insertMenu.push(ToolbarControls.CANVAS_EMBED);
  }
  if (contentType !== 'email' && useVideo) {
    insertMenu.push(ToolbarControls.CANVAS_VIDEO);
  }
  insertMenu.push(ToolbarControls.CANVAS_TABLE);
  if (useCallout) {
    insertMenu.push(ToolbarControls.CANVAS_CALLOUT);
  }
  if (!useCharmapButton) {
    insertMenu.push(ToolbarControls.CANVAS_CHARMAP);
  }
  if (contentType !== 'email' && useIcon) {
    insertMenu.push(ToolbarControls.CANVAS_ICON);
  }
  if (contentType !== 'email') {
    insertMenu.push(ToolbarControls.CANVAS_ANCHOR);
  }
  if (editContext === 'post_body') {
    insertMenu.push(ToolbarControls.CANVAS_READ_MORE);
  }
  insertMenu.push(ToolbarControls.CANVAS_HR);
  insertMenu.push(ToolbarControls.CANVAS_NONBREAKING_SPACE);
  return insertMenu.filter(control => includesToolbarControl(toolbarControls, control));
}
function getAdvancedMenu(toolbarControls, {
  useSourceCode,
  useGoogleDocs
}) {
  const advancedMenu = [ToolbarControls.CANVAS_VISUAL_BLOCKS];
  if (useSourceCode) {
    advancedMenu.unshift(ToolbarControls.CANVAS_SOURCE_CODE);
  }
  if (useGoogleDocs) {
    advancedMenu.push(ToolbarControls.CANVAS_IMPORT_GOOGLE_DOC);
  }
  return advancedMenu.filter(control => includesToolbarControl(toolbarControls, control));
}
// Canvas toolbar with prioritize controls for collapsing,
// responsiveness and multi-row configuration
export const buildCanvasToolbar = (toolbarOptions = {}) => (options = {}) => {
  const {
      toolbarControls: _toolbarControls = [],
      responsive = true,
      multiRow = false
    } = toolbarOptions,
    rest = _objectWithoutPropertiesLoose(toolbarOptions, _excluded);

  // [enabledFeatures] is passed down from the app into the config
  const toolbarControls = getToolbarControls(options.enabledFeatures) || _toolbarControls;
  options = Object.assign({}, rest, options);
  const topRow = [];
  const bottomRow = [];
  if (includesToolbarControl(toolbarControls, ToolbarControls.CANVAS_BLOCK_FORMAT)) {
    topRow.push({
      controls: [{
        type: ToolbarControlTypes.SELECT,
        control: ToolbarControls.CANVAS_BLOCK_FORMAT
      }]
    });
  }
  const {
    formatControls,
    hasMoreMenu
  } = getFormatControls(toolbarControls, responsive, options);
  if (formatControls.length) {
    const formatControlGroup = {
      controls: formatControls
    };
    if (responsive && hasMoreMenu) {
      formatControlGroup.responsivePriority = 0;
      formatControlGroup.boundary = 3;
    }
    topRow.push(formatControlGroup);
  }
  const insertControls = getInsertControls(toolbarControls, options);
  const insertMenu = getInsertMenu(toolbarControls, options);
  if (insertMenu.length) {
    insertControls.push({
      type: ToolbarControlTypes.INSERT_MENU,
      menuItems: insertMenu
    });
  }
  if (insertControls.length) {
    const insertControlGroup = {
      controls: insertControls
    };
    if (responsive && insertMenu.length) {
      insertControlGroup.responsivePriority = 1;
      insertControlGroup.boundary = 0;
    }
    bottomRow.push(insertControlGroup);
  }
  if (options.usePersonalization && !options.hidePersonalizationButton && includesToolbarControl(toolbarControls, ToolbarControls.CANVAS_PERSONALIZE)) {
    bottomRow.push({
      controls: [{
        type: ToolbarControlTypes.BUTTON,
        control: ToolbarControls.CANVAS_PERSONALIZE
      }]
    });
  }
  const advancedMenu = getAdvancedMenu(toolbarControls, options);
  if (advancedMenu.length) {
    bottomRow.push({
      controls: [{
        type: ToolbarControlTypes.ADVANCED_MENU,
        menuItems: advancedMenu
      }]
    });
  }
  const addDividersReducer = (acc, group, idx) => {
    if (idx !== 0) {
      acc.push({
        controls: [{
          type: ToolbarControlTypes.DIVIDER
        }]
      });
    }
    acc.push(group);
    return acc;
  };
  if (multiRow) {
    return [topRow.reduce(addDividersReducer, []), bottomRow.reduce(addDividersReducer, [])];
  }
  return [...topRow, ...bottomRow].reduce(addDividersReducer, []);
};
export const buildMultiRowCanvasToolbar = toolbarOptions => buildCanvasToolbar(Object.assign({
  responsive: true
}, toolbarOptions, {
  multiRow: true
}));